<script setup lang="ts">
import { computed } from 'vue';
import type { FilterChip } from '@/types/browser';
import type { WorldInterface } from '@/types/worlds';
import { usePredicateStore } from '@/stores/predicate-store';
import { useAuthDataStore } from '@/stores/auth-data';
import { worldChips, appendWorld, removeWorldChip, WORLD_CHIP_PREFIX } from '@/appdata/world-filters';
import FilterControl from '@/components/atoms/element-filters/FilterControl.vue';
import FilterField from '@/components/atoms/element-filters/FilterField.vue';
import WorldFinder from '@/components/atoms/WorldFinder.vue';

// The sibling of SourceFilterControl, and a DIFFERENT question. Source matches the element's own
// `source` property — where it was published. World finds elements linked to a campaign setting.
// An element can match one, both or neither, so the two filters combine rather than compete:
// useElementPredicates emits them as separate `source.uuid` and `world.uuid` predicates.
//
// Both are multi-select, and they share their selection rules (@/appdata/world-filters) so the two
// cannot drift on what picking an already-picked world means.
//
// This writes predicateStore.worlds, the slot useElementPredicates has always read. Nothing in the
// predicate layer changes — the slot simply had no control before this. Back when it had none,
// every list cleared it on mount to keep a scope written elsewhere (ItemFinder, guided setup) from
// filtering the data manager silently; that guard now lives once in DataManagement.onMounted,
// because per-list it also discarded whatever the user picked here whenever they changed category.
const props = defineProps<{ label: string; placeholder?: string }>();

const predicateStore = usePredicateStore();
const auth = useAuthDataStore();

// Gated here rather than by each host: ten lists each repeating the guard is ten chances for one
// to drift. Hosts still count predicateStore.worlds in `filtersActive` unconditionally, which is
// safe — a user who cannot reach this control never puts anything in the slot.
const canFilterByWorld = computed(() => auth.canSelectWorld);

const chips = computed<FilterChip[]>(() =>
  worldChips(predicateStore.worlds as WorldInterface[], WORLD_CHIP_PREFIX, props.label),
);

const onWorldSelected = (event: any): void => {
  if (event?.item) {
    predicateStore.worlds = appendWorld(predicateStore.worlds as WorldInterface[], event.item);
  }
};

// Removes just the one its chip names, rather than clearing the filter.
const remove = (chip: FilterChip): void => {
  predicateStore.worlds = removeWorldChip(
    predicateStore.worlds as WorldInterface[], chip, WORLD_CHIP_PREFIX,
  );
};
</script>

<template>
  <FilterControl
    v-if="canFilterByWorld"
    :label="label"
    :chips="chips"
    :multiple="true"
    @remove="remove"
  >
    <FilterField :label="label">
      <!-- No include-all, like CharacterList's world filter: the Campaign chip offers the user's
           campaigns only. The API answers an owned-only lookup with "my worlds" — the user's own
           and the ones they play in (WorldRepository::index in dicey-api) — so a user invited to
           a world still finds it. The finder's scope is its own: it no longer shares an
           owned-only flag with SourceFilterControl's finder mounted alongside.

           Always rendered: multi-select needs the finder reachable to add the next world. -->
      <WorldFinder
        :placeholder="placeholder"
        @world-selected="onWorldSelected"
      />
    </FilterField>
  </FilterControl>
</template>
